import {
  column,
  Css,
  dateColumn,
  dateRangeFilter,
  DateRangeFilterValue,
  emptyCell,
  FilterDefs,
  GridColumn,
  GridDataRow,
  GridTableLayout,
  multiFilter,
  numericColumn,
  PageHeaderLayout,
  RowStyles,
  simpleHeader,
  singleFilter,
  useGridTableLayoutState,
  useModal,
} from "@homebound/beam";
import { useCallback, useMemo } from "react";
import { chipCell, dateCell, priceCell, tagCell } from "src/components";
import { CommitmentsFilter, DateOperation, Maybe, Stage } from "src/generated/graphqlTypes";
import { useRouteParams } from "src/hooks/useRouteParams";
import { useStripStage } from "src/hooks/useStripStage";
import {
  CommitmentsPage_ChangeOrderFragment,
  CommitmentsPage_CommitmentFragment,
  CommitmentsPageQuery,
  useCommitmentsPageMetadataQuery,
  useCommitmentsPageQuery,
} from "src/routes/projects/commitments/CommitmentsPage.generated";
import { NewCommitmentModal } from "src/routes/projects/commitments/components/NewCommitmentModal";
import { useProjectContext } from "src/routes/projects/context/ProjectContext";
import { ProjectParams } from "src/routes/routesDef";
import { createCommitmentChangeOrderUrl, createCommitmentUrl, createReleaseToTradesConfirmUrl } from "src/RouteUrls";
import { commitmentStatusToTagTypeMapper, foldEnum, stageCodeToNameMapper } from "src/utils";
import { hasCompleteDateRange } from "src/utils/temporal";

export function CommitmentsPage() {
  useStripStage();
  const { projectId } = useRouteParams<ProjectParams>();
  const { __typename, latestActiveStage, market, name } = useProjectContext();
  const marketId = market!.id; // any project that has commitments will have a market

  const { data: metadata } = useCommitmentsPageMetadataQuery({ variables: { projectId }, fetchPolicy: "cache-first" });
  const filterDefs: FilterDefs<FilterOptions> = useMemo(() => {
    const stages = [Stage.PreConExecution, Stage.Construction];

    return {
      stage: singleFilter({
        options: [...stages.map((s) => ({ id: s, name: stageCodeToNameMapper[s] }))],
        defaultValue: stages.includes(latestActiveStage) ? latestActiveStage : undefined,
        getOptionValue: (o) => o.id,
        getOptionLabel: (o) => o.name,
      }),
      status: multiFilter({
        options: metadata?.commitmentStatuses ?? [],
        getOptionValue: ({ code }) => code,
        getOptionLabel: ({ name }) => name,
      }),
      costCodes: multiFilter({
        options:
          metadata?.commitments
            .flatMap(({ costCodes }) => costCodes)
            .uniqueByKey("id")
            .sortByKey("displayName") ?? [],
        getOptionLabel: (cc) => cc.displayName,
        getOptionValue: (cc) => cc.id,
        label: "Cost codes",
      }),
      releasedAt: dateRangeFilter<string>({ label: "Released", testFieldLabel: "Released" }),
    };
  }, [latestActiveStage, metadata]);

  const layoutState = useGridTableLayoutState<FilterOptions>({
    persistedFilter: { filterDefs, storageKey: `commitmentsPageFilter_${projectId}` },
    search: "client",
  });
  const { openModal } = useModal();

  const query = useCommitmentsPageQuery({
    variables: { filter: mapToCommitmentsFilter(projectId, layoutState.filter) },
  });

  const columns = useMemo(() => createColumns(), []);
  const rowStyles = useMemo(() => createRowStyles(projectId), [projectId]);
  const createRows = useCallback((data: CommitmentsPageQuery | undefined) => mapRows(data?.commitments ?? []), []);

  return (
    <PageHeaderLayout
      pageHeader={{
        title: __typename === "StandaloneProject" ? "Commitments" : "Purchase Orders",
        documentTitleSuffix: name,
        actions: [
          {
            kind: "menu",
            trigger: { label: "Create", variant: "primary" },
            items: [
              {
                label: "Add New",
                onClick: () => {
                  openModal({ content: <NewCommitmentModal projectId={projectId} marketId={marketId} /> });
                },
              },
              { label: "Release To Trades", onClick: createReleaseToTradesConfirmUrl(projectId) },
            ],
          },
        ],
      }}
    >
      <GridTableLayout
        layoutState={layoutState}
        tableProps={{
          query,
          createRows,
          columns,
          rowStyles,
          sorting: { on: "client", initial: ["po#", "DESC"] },
          persistCollapse: "project-commitments-table",
          style: { rowHeight: "fixed", allWhite: true },
          emptyFallback: "No commitments found",
        }}
      />
    </PageHeaderLayout>
  );
}

type FilterOptions = Omit<CommitmentsFilter, "stage"> & {
  releasedAt: DateRangeFilterValue<string>;
  stage?: Maybe<Stage>;
};

type HeaderRow = { kind: "header" };
type CommitmentRow = { kind: "commitment"; data: CommitmentsPage_CommitmentFragment };
type ChangeOrderRow = { kind: "changeOrder"; data: CommitmentsPage_ChangeOrderFragment };
type TotalsRow = { kind: "totals"; id: string; data: { name: string } & CommitmentTotals };
type Row = HeaderRow | CommitmentRow | ChangeOrderRow | TotalsRow;
type CommitmentTotals = {
  committedInCents: number;
  billedInCents: number;
  paidInCents: number;
  balanceInCents: number;
  remainingInCents: number;
};

function createRowStyles(projectId: string): RowStyles<Row> {
  return {
    commitment: { rowLink: (row) => createCommitmentUrl(projectId, row.data.id) },
    changeOrder: { rowLink: (row) => createCommitmentChangeOrderUrl(projectId, row.data.commitment.id, row.data.id) },
  };
}

function createColumns(): GridColumn<Row>[] {
  return [
    column<Row>({
      id: "po#",
      name: "PO#",
      canHide: false,
      totals: "Totals",
      header: "PO#",
      commitment: ({ accountingNumber }) => ({ content: () => `#${accountingNumber}`, sortValue: accountingNumber }),
      changeOrder: ({ accountingNumber }) => ({ content: () => `#${accountingNumber}`, sortValue: accountingNumber }),
      mw: "120px",
    }),
    column<Row>({
      id: "tradePartner",
      name: "Trade Partner",
      canHide: false,
      totals: emptyCell,
      header: "Trade Partner",
      commitment: (commitment) => commitment.name,
      changeOrder: (changeOrder) => ({
        content: <div css={Css.pl2.$}>{changeOrder.name}</div>,
        value: changeOrder.name,
      }),
      mw: "160px",
    }),
    column<Row>({
      id: "itemCode",
      name: "Item Code",
      totals: emptyCell,
      header: "Item Code",
      commitment: ({ lineItems }) => {
        const chips = lineItems.map((li) => li.projectItem.item.displayName).sort();
        return chipCell(chips);
      },
      changeOrder: ({ lineItems }) => {
        const chips = lineItems.map((li) => li.projectItem.item.displayName).sort();
        return chipCell(chips);
      },
      mw: "200px",
    }),
    column<Row>({
      id: "type",
      name: "Type",
      totals: emptyCell,
      header: "Type",
      commitment: ({ commitmentType }) => foldEnum(commitmentType.code, { PURCHASE_ORDER: "PO", SUBCONTRACT: "SUB" }),
      changeOrder: () => "CO",
      mw: "80px",
    }),
    dateColumn<Row>({
      id: "lastEdited",
      name: "Last Edited",
      totals: emptyCell,
      header: "Last Edited",
      commitment: ({ updatedAt }) => dateCell(updatedAt),
      changeOrder: ({ updatedAt }) => dateCell(updatedAt),
      mw: "110px",
    }),
    dateColumn<Row>({
      id: "signedOn",
      name: "Signed On",
      totals: emptyCell,
      header: "Signed On",
      commitment: ({ executionDate }) => dateCell(executionDate),
      changeOrder: ({ executionDate }) => dateCell(executionDate),
      mw: "110px",
    }),
    numericColumn<Row>({
      totals: ({ committedInCents }) => priceCell({ valueInCents: committedInCents }),
      id: "committed",
      name: "Committed",
      header: "Committed",
      commitment: ({ committedInCents }) => priceCell({ valueInCents: committedInCents }),
      changeOrder: ({ committedInCents }) => priceCell({ valueInCents: committedInCents }),
      mw: "120px",
    }),
    numericColumn<Row>({
      totals: ({ billedInCents }) => priceCell({ valueInCents: billedInCents }),
      id: "billed",
      name: "Billed",
      header: "Billed",
      commitment: ({ pendingBilledInCents }) => priceCell({ valueInCents: pendingBilledInCents }),
      changeOrder: ({ pendingBilledInCents }) => priceCell({ valueInCents: pendingBilledInCents }),
      mw: "110px",
    }),
    numericColumn<Row>({
      totals: ({ remainingInCents }) => priceCell({ valueInCents: remainingInCents }),
      id: "remaining",
      name: "Remaining",
      header: "Remaining",
      commitment: (commitment) => priceCell({ valueInCents: commitment.pendingRemainingInCents }),
      changeOrder: (changeOrder) => priceCell({ valueInCents: changeOrder.pendingRemainingInCents }),
      mw: "120px",
    }),
    numericColumn<Row>({
      totals: ({ paidInCents }) => priceCell({ valueInCents: paidInCents }),
      id: "paidToDate",
      name: "Paid to Date",
      header: "Paid to Date",
      commitment: ({ paidInCents }) => priceCell({ valueInCents: paidInCents }),
      changeOrder: ({ paidInCents }) => priceCell({ valueInCents: paidInCents }),
      mw: "120px",
    }),
    numericColumn<Row>({
      totals: ({ balanceInCents }) => priceCell({ valueInCents: balanceInCents }),
      id: "balanceDue",
      name: "Balance Due",
      header: "Balance Due",
      commitment: ({ balanceInCents }) => priceCell({ valueInCents: balanceInCents }),
      changeOrder: ({ balanceInCents }) => priceCell({ valueInCents: balanceInCents }),
      mw: "120px",
    }),
    column<Row>({
      id: "status",
      name: "Status",
      totals: emptyCell,
      header: "Status",
      commitment: ({ status, statusText }) => tagCell(commitmentStatusToTagTypeMapper[status], statusText),
      changeOrder: ({ status, statusText }) => tagCell(commitmentStatusToTagTypeMapper[status], statusText),
      mw: "88px",
    }),
  ];
}

function mapRows(commitments: CommitmentsPage_CommitmentFragment[]): GridDataRow<Row>[] {
  return [
    { kind: "totals", id: "totals", data: { name: "Totals", ...sumCommitments(commitments) } },
    simpleHeader,
    ...commitments.map((c) => ({
      kind: "commitment" as const,
      id: c.id,
      data: c,
      children: c.changeOrders.map((co) => ({ kind: "changeOrder" as const, id: co.id, data: co })),
    })),
  ];
}

export function sumCommitments(commitments: CommitmentsPage_CommitmentFragment[]): CommitmentTotals {
  const allCommitments = commitments.flatMap(({ changeOrders, ...contract }) => [contract, ...changeOrders]);

  return allCommitments.reduce(
    (acc, contract) => {
      return {
        remainingInCents: acc.remainingInCents + (contract.pendingRemainingInCents || 0),
        committedInCents: acc.committedInCents + (contract.committedInCents || 0),
        billedInCents: acc.billedInCents + (contract.pendingBilledInCents || 0),
        paidInCents: acc.paidInCents + (contract.paidInCents || 0),
        balanceInCents: acc.balanceInCents + (contract.balanceInCents || 0),
      };
    },
    { remainingInCents: 0, committedInCents: 0, billedInCents: 0, paidInCents: 0, balanceInCents: 0 },
  );
}

function mapToCommitmentsFilter(projectId: string, filter: FilterOptions): CommitmentsFilter {
  return {
    projectId,
    status: filter.status,
    costCodes: filter.costCodes,
    stage: filter.stage ? [filter.stage] : undefined,
    executionDate: hasCompleteDateRange(filter.releasedAt?.value)
      ? { op: DateOperation.Between, value: filter.releasedAt.value.from!, value2: filter.releasedAt.value.to! }
      : undefined,
  };
}
